import { useQuery } from '@apollo/client';
import { graphql } from '../gql';
import { useEffect, useMemo } from 'react';
import { GetProgramsQuery } from '../gql/graphql';
import { getPaginatedNodes } from './utils';

import {
  handleValidationErrors,
  validatePaginatedEdgesResponse,
  yep,
} from '@converge/utils';

const GET_PROGRAMS_SCHEMA = yep.object().shape({
  programs: validatePaginatedEdgesResponse(
    'Program',
    yep.object().shape({
      name: yep.string().nullToDefault('N/A').required(),
      uuid: yep.string().required(),
    })
  ),
});

const GET_PROGRAMS = graphql(`
  query getPrograms($after: Int, $first: Int) {
    programs(pagination: { first: $first, after: $after }) {
      pageInfo {
        totalResults
        hasNextPage
        pageNumber
      }
      edges {
        cursor
        node {
          name
          uuid
          partnered
          openMarket
        }
      }
    }
  }
`);

export function useGetProgramsQuery({
  first = 2000,
  enabled = true,
}: {
  first?: number;
  enabled?: boolean;
}) {
  const { loading, error, data, fetchMore } = useQuery(GET_PROGRAMS, {
    variables: {
      after: 0,
      first,
    },
    skip: !enabled,
    onCompleted: (data) => {
      if (data.programs.pageInfo.hasNextPage) {
        fetchMore({
          variables: {
            after: data.programs.pageInfo.pageNumber + 1,
            first,
          },
          updateQuery: (previousData, { fetchMoreResult: newData }) => {
            const mergedData: GetProgramsQuery = {
              ...previousData,
              ...newData,
              programs: {
                ...previousData.programs,
                ...newData.programs,
                pageInfo: {
                  ...previousData.programs.pageInfo,
                  ...newData.programs.pageInfo,
                },
                edges: [
                  ...previousData.programs.edges,
                  ...newData.programs.edges,
                ],
              },
            };
            return mergedData;
          },
        });
      }
    },
  });

  const programs = useMemo(() => {
    if (!data) {
      return [];
    }
    try {
      const validatedData = GET_PROGRAMS_SCHEMA.validateSync(data, {
        strict: false,
        abortEarly: false,
      });
      return validatedData.programs.edges.map((edge) => edge.node);
    } catch (cause) {
      console.warn('useGetProgramsQuery', cause);
      return getPaginatedNodes(data.programs);
    }
  }, [data]);

  useEffect(() => {
    if (!data) {
      return;
    }
    try {
      GET_PROGRAMS_SCHEMA.validateSync(data, {
        strict: true,
      });
    } catch (cause) {
      handleValidationErrors({
        cause,
        source: 'useGetProgramQuery',
        inputData: data,
        throwError: false,
      });
    }
  }, [data]);

  return {
    loading,
    error,
    data,
    programs,
  };
}

export type ProgramDetails = ReturnType<
  typeof useGetProgramsQuery
>['programs'][0];
